/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext, HousePlanFinderListItem } from '@gh/builder-shared';
import { HlpDetails, QuotationBase, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  DictionaryItem,
  EnumDictionary,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
  IS_REGION_US,
  NumberTypes,
  toAppFormGroup,
  updateChildrenSchema,
  whenCreateMode,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { AutocompleteLoadMoreEvent } from '@gh/core-ui';
import { coalesce, isNotNil } from '@gh/core-util';
import { QuoteDetailsBusinessService } from '../../../../../leads';
import {
  HlpQuoteDetailsMasterInfo,
  QuoteDetailsDerivedInfo,
  QuoteSalesTaxRate,
} from '../../../../resources/hlp-quote.model';
import { QuoteDetailsSettings } from '../../../../../leads/components/quote-details/quote-details.state';
import { HlpDetailsBusinessService } from '../../../../resources/hlp-details-business.service';

const getMasterInfoSchemaConfig = (masterInfo: HlpQuoteDetailsMasterInfo, details: HlpDetails) => (
  {
    children: {
      description: { required: true },
      priceBook:   { required: true, enabled: [whenCreateMode()] },
      base: { required: true,
        enabled:        [whenCreateMode(), isNotNil(
          masterInfo.priceBook)],
      },
      baseHousePlan: { required: true,
        enabled: [whenCreateMode(), isNotNil(
          masterInfo.base)],
      },
      housePlan:     void 0,
      siteAddress:   { enabled: false },
      salesTaxRates: void 0,
      isRounded:     void 0,
    },
  }
);

@Component({
  standalone: false,
  selector:        'gh-hlp-quote-details-top-form',
  templateUrl:     'hlp-quote-details-top-form.component.html',
  styleUrls:      ['hlp-quote-details-top-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HlpQuoteDetailsTopFormComponent extends AbstractSnapshotFormComponent<HlpQuoteDetailsMasterInfo> implements OnInit {

  @Input() committed: HlpQuoteDetailsMasterInfo;
  @Input() settings: QuoteDetailsSettings;
  @Input() derivedInfo: QuoteDetailsDerivedInfo;

  @Output() searchHousePlan = new EventEmitter<void>();
  @Output() openGto = new EventEmitter<any>();
  @Output() openTops = new EventEmitter<any>();
  @Output() openHpe = new EventEmitter<void>();
  @Output() openStr = new EventEmitter<void>();
  @Output() onMasterInfoChanged = new EventEmitter<FormState<HlpQuoteDetailsMasterInfo>>();

  quotationBase: EnumDictionary<QuotationBase>;

  constructor(
    private route: ActivatedRoute,
    private formStateBuilder: FormStateBuilder,
    private businessService: QuoteDetailsBusinessService,
    private hlpDetailsService: HlpDetailsBusinessService,
    private userContext: BuilderUserContext,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  get isSalesEstimate(): boolean {
    return this.committed.base === QuotationBase.SalesEstimate;
  }

  get isQuickQuote(): boolean {
    return this.committed.base === QuotationBase.QuickQuote;
  }

  get housePlanCode(): string {
    const housePlan = this.committed.housePlan;

    return housePlan ? housePlan.code : '';
  }

  get canOpenSe(): boolean {
    return !this.userContext.hasSystemRole(SystemRoleBit.SalesPerson) || this.settings.isSalesEstimateShown;
  }

  get usRegion(): boolean {
    return IS_REGION_US;
  }

  get auOrNzRegion(): boolean {
    return IS_REGION_AU_OR_NZ;
  }

  get isDisabledGto(): boolean {
    return this.state.value.base !== QuotationBase.QuickQuote;
  }

  getSalesTaxRatesLabel(str: QuoteSalesTaxRate[]): string {
    return str.map((rate) => NumberTypes.Rate.format(rate.ratePct) + '%').join('; ');
  }

  getHousePlanTitle(hp?: HousePlanFinderListItem): string {
    return hp ? `${ hp.code }: ${ hp.name }` : '';
  }

  ngOnInit(): void {
    super.ngOnInit();
    const { quotationBase } = getRouteInitData(this.route);

    this.quotationBase = quotationBase.filter((item: DictionaryItem<QuotationBase>) => coalesce(
      {
        [QuotationBase.SalesEstimate]: this.settings.useSalesEstimate,
        [QuotationBase.QuickQuote]:    this.settings.isQuickQuotesAllowed,
      }[item.id],
      true,
    ));
    if (this.hlpDetailsService.needValidate) {
      this.form.markAllAsTouched();
      this.form.group.markAllAsTouched();
      this.changeDetectorRef.detectChanges();
    }

    this.form.valueChanges.subscribe(() => {
        const value = this.form.getControl('baseHousePlan').value;
        this.hlpDetailsService.hlpDetails.baseHousePlanName = value ? value.name : '';
        this.hlpDetailsService.hlpDetails.baseHousePlanCode = value ? value.code : '';
        this.hlpDetailsService.hlpDetails.baseHousePlanId = value ? value.housePlanId : '';
        this.onMasterInfoChanged.emit();
        if (!this.hlpDetailsService.needValidate) {
          return;
        }
        this.form.markAllAsTouched();
        this.form.group.markAllAsTouched();
        this.changeDetectorRef.detectChanges();
    });
    this.hlpDetailsService.masterInfoForm = this.form;
    this.hlpDetailsService.masterInfoFormChangeDetectorRef = this.changeDetectorRef;
  }

  loadPriceBook(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findPriceBooksByName(event.query));
  }

  onOpenTops(): void {
    this.openTops.emit({
      formMode: this.mode,
    });
  }

  onOpenGto(): void {
    this.openGto.emit({
      isDisabledGto: this.isDisabledGto,
    });
  }

  protected buildForm(snapshot: FormStateSnapshot<HlpQuoteDetailsMasterInfo>): FormState<HlpQuoteDetailsMasterInfo> {
    const masterInfo = getFormStateSnapshotValue(snapshot);

    return this.formStateBuilder.buildPlain({
      snapshot,
      mode:     this.mode,
      enabled:  whenCreateOrEditMode(),
      children: getMasterInfoSchemaConfig(masterInfo, this.hlpDetailsService.hlpDetails).children,
    });
  }

  protected afterRestored(): void {
    this.form.getControl('baseHousePlan').updateValueAndValidity();
    updateChildrenSchema(
      toAppFormGroup(this.form.group),
      getMasterInfoSchemaConfig(this.form.group.value, this.hlpDetailsService.hlpDetails).children,
    );
  }
}
